import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import {
  DH_TYPE_OPTIONS,
  DRIVER_CABIN_TYPE_OPTIONS,
  RegistrationSelectOption,
  SUSPENSION_TYPE_OPTIONS,
} from "../data";
import {
  selectIsAddModalOpen,
  selectRegistrationForm,
  selectRegistrationFormDropDowns,
  selectRegistrationSaving,
} from "../redux/selector";
import { registrationActions } from "../redux/slice";
import {
  REGISTRATION_FORM_FIELD_KINDS,
  RegistrationForm,
  RegistrationFormDropDownKey,
} from "../types";

export type RegistrationFieldConfig = {
  key: keyof RegistrationForm;
  label: string;
  type: "select" | "text" | "textarea";
  options?: RegistrationSelectOption[];
  fullWidth?: boolean;
  dropDownKey?: RegistrationFormDropDownKey;
};

export const sanitizeRegistrationFormValue = (
  name: keyof RegistrationForm,
  value: string,
) => {
  if (REGISTRATION_FORM_FIELD_KINDS[name] === "number") {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

const STATIC_ROWS: RegistrationFieldConfig[][] = [
  [
    {
      key: "driverCabinType",
      label: "DRIVER_CABIN_TYPE",
      type: "select",
      options: DRIVER_CABIN_TYPE_OPTIONS,
    },
    {
      key: "tire",
      label: "TIRE",
      type: "select",
      dropDownKey: "tires",
    },
  ],
  [
    {
      key: "suspensionType",
      label: "SUSPENSION_TYPE",
      type: "select",
      options: SUSPENSION_TYPE_OPTIONS,
    },
    { key: "weight", label: "WEIGHT", type: "text" },
  ],
  [
    { key: "axlesMeasure", label: "AXLES_MEASURE", type: "text" },
    { key: "lowerLimit", label: "LOWER_LIMIT", type: "text" },
    { key: "upperLimit", label: "UPPER_LIMIT", type: "text" },
  ],
  [
    { key: "frontLoad", label: "FRONT_LOAD", type: "text" },
    { key: "rearAxlesMeasure", label: "REAR_AXLES_MEASURE", type: "text" },
  ],
  [{ key: "notes", label: "NOTES", type: "textarea", fullWidth: true }],
  [
    {
      key: "dhType",
      label: "DH_TYPE",
      type: "select",
      options: DH_TYPE_OPTIONS,
    },
    {
      key: "bodyType",
      label: "BODY_TYPE",
      type: "select",
      dropDownKey: "bodyTypes",
    },
  ],
];

const REQUIRED_FIELDS: Array<keyof RegistrationForm> = [
  "driverCabinType",
  "tire",
  "suspensionType",
  "weight",
  "axlesMeasure",
  "lowerLimit",
  "upperLimit",
  "frontLoad",
  "rearAxlesMeasure",
  "dhType",
  "bodyType",
];

const P = "REGISTRATION.MODAL";

export const useAddRegistrationModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectRegistrationForm);
  const saving = useSelector(selectRegistrationSaving);
  const { tires, bodyTypes } = useSelector(selectRegistrationFormDropDowns);

  useEffect(() => {
    if (!open) return;
    dispatch(registrationActions.getFormDropDownList());
  }, [open, dispatch]);

  const tireOptions = tires.items.map((row) => ({
    value: row.tireCode,
    label: row.tireTypeName || row.tireCode,
  }));
  const bodyTypeOptions = bodyTypes.items.map((row) => ({
    value: String(row.bodyTypeId),
    label: row.bodyTypeName,
  }));

  const rows = STATIC_ROWS.map((row) =>
    row.map((field) => {
      if (field.dropDownKey === "tires") {
        return { ...field, options: tireOptions };
      }
      if (field.dropDownKey === "bodyTypes") {
        return { ...field, options: bodyTypeOptions };
      }
      return field;
    }),
  );

  const onClose = () => {
    if (saving) return;
    dispatch(registrationActions.closeAddModal());
  };

  const setField = (name: keyof RegistrationForm, value: string) =>
    dispatch(
      registrationActions.setFormField({
        name,
        value: sanitizeRegistrationFormValue(name, value),
      }),
    );

  const placeholder = (
    label: string,
    type: RegistrationFieldConfig["type"],
  ) =>
    t(
      `${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`,
    );

  const fieldLabel = (label: string) => t(`${P}.FIELDS.${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: RegistrationSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isNumberField = (key: keyof RegistrationForm) =>
    REGISTRATION_FORM_FIELD_KINDS[key] === "number";

  const onSubmit = () => dispatch(registrationActions.addRegistration());

  const onDropDownScroll = (dropDownKey?: RegistrationFormDropDownKey) => {
    if (!dropDownKey) return;
    dispatch(registrationActions.getFormDropDownNextPage(dropDownKey));
  };

  const hasMoreDropDownItems = (dropDownKey?: RegistrationFormDropDownKey) => {
    if (!dropDownKey) return false;
    const dropDown = dropDownKey === "tires" ? tires : bodyTypes;
    return (
      !dropDown.loading && dropDown.meta.currentPage < dropDown.meta.totalPages
    );
  };

  return {
    t,
    open,
    form,
    saving,
    rows,
    isValid,
    addTitle: t(`${P}.ADD_TITLE`),
    onClose,
    setField,
    placeholder,
    fieldLabel,
    optionLabel,
    isNumberField,
    onSubmit,
    onDropDownScroll,
    hasMoreDropDownItems,
  };
};
